/* ==========================================================================
   NUKKA — carousel.js
   Scroll-snap carousel with arrows, dots, and pointer-drag.
   Markup: .carousel > .carousel__viewport > .carousel__track > .carousel__slide
   Controls: [data-carousel-prev] [data-carousel-next] [data-carousel-dots]
   Optional: data-slide-ms="350" animates arrow/autoplay moves over that many ms
   instead of the browser's (slower, fixed-speed) smooth scrolling.
   ========================================================================== */
(function () {
  'use strict';
  var N = window.NUKKA || (window.NUKKA = {});

  function slideStep(track) {
    var slide = track.querySelector('.carousel__slide');
    if (!slide) return track.clientWidth;
    var gap = parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap || 0) || 0;
    return slide.getBoundingClientRect().width + gap;
  }

  // Timed scroll for [data-slide-ms]. Snap and CSS smooth-scrolling are paused while it runs,
  // otherwise they fight the per-frame scrollLeft updates.
  function glideTo(track, left, ms) {
    var start = track.scrollLeft, dist = left - start, t0 = null;
    if (!dist) return;
    track.style.scrollSnapType = 'none';
    track.style.scrollBehavior = 'auto';
    function frame(now) {
      if (t0 === null) t0 = now;
      var p = Math.min(1, (now - t0) / ms);
      var e = p < 0.5 ? 4 * p * p * p : 1 - Math.pow(-2 * p + 2, 3) / 2;
      track.scrollLeft = start + dist * e;
      if (p < 1) { requestAnimationFrame(frame); }
      else { track.style.scrollSnapType = ''; track.style.scrollBehavior = ''; }
    }
    requestAnimationFrame(frame);
  }

  N.register(function (scope) {
    scope.querySelectorAll('[data-carousel]').forEach(function (root) {
      if (root._init) return; root._init = true;
      var track = root.querySelector('.carousel__track');
      if (!track) return;
      // Arrow buttons may sit in the section header, outside [data-carousel] —
      // fall back to the enclosing section when they're not inside the root.
      var section = root.closest('.shopify-section') || root.closest('[data-section-id]') || root.parentElement;
      var prev = root.querySelector('[data-carousel-prev]') || (section && section.querySelector('[data-carousel-prev]'));
      var next = root.querySelector('[data-carousel-next]') || (section && section.querySelector('[data-carousel-next]'));
      var dotsWrap = root.querySelector('[data-carousel-dots]');

      var nav = prev && prev.closest('[data-carousel-nav]');

      function update() {
        var max = track.scrollWidth - track.clientWidth - 1;
        var scrollable = max > 0;
        if (nav) nav.classList.toggle('is-hidden', !scrollable);
        if (dotsWrap) dotsWrap.classList.toggle('is-hidden', !scrollable);
        if (prev) prev.disabled = track.scrollLeft <= 1;
        if (next) next.disabled = track.scrollLeft >= max;
        if (dotsWrap) {
          var dots = dotsWrap.children;
          var step = slideStep(track) || 1;
          var idx = Math.round(track.scrollLeft / step);
          for (var d = 0; d < dots.length; d++) {
            dots[d].setAttribute('aria-current', d === idx ? 'true' : 'false');
          }
        }
      }

      var glideMs = parseInt(root.getAttribute('data-slide-ms'), 10) || 0;
      function moveTo(left) {
        var max = track.scrollWidth - track.clientWidth;
        left = Math.max(0, Math.min(max, left));
        if (glideMs > 0) glideTo(track, left, glideMs);
        else track.scrollTo({ left: left, behavior: 'smooth' });
      }

      // Next/previous slide boundary from the current position (keeps glides aligned to slides).
      function stepBy(dir) {
        var step = slideStep(track) || 1;
        moveTo((Math.round(track.scrollLeft / step) + dir) * step);
      }

      if (prev) prev.addEventListener('click', function () { stepBy(-1); });
      if (next) next.addEventListener('click', function () { stepBy(1); });

      // Build dots (one per slide)
      if (dotsWrap) {
        var slides = track.querySelectorAll('.carousel__slide');
        dotsWrap.innerHTML = '';
        slides.forEach(function (s, i) {
          var b = document.createElement('button');
          b.className = 'carousel__dot';
          b.type = 'button';
          b.setAttribute('aria-label', 'Go to item ' + (i + 1));
          b.addEventListener('click', function () { moveTo(slideStep(track) * i); });
          dotsWrap.appendChild(b);
        });
      }

      var raf;
      track.addEventListener('scroll', function () {
        if (raf) cancelAnimationFrame(raf);
        raf = requestAnimationFrame(update);
      }, { passive: true });
      window.addEventListener('resize', update, { passive: true });
      update();

      // Pointer drag (mouse only; touch uses native scroll)
      var down = false, startX = 0, startScroll = 0, moved = false;
      track.addEventListener('pointerdown', function (e) {
        if (e.pointerType === 'touch') return;
        down = true; moved = false;
        startX = e.clientX; startScroll = track.scrollLeft;
        track.style.cursor = 'grabbing';
      });
      track.addEventListener('pointermove', function (e) {
        if (!down) return;
        var dx = e.clientX - startX;
        if (Math.abs(dx) > 4) moved = true;
        track.scrollLeft = startScroll - dx;
      });
      function end() { down = false; track.style.cursor = ''; }
      track.addEventListener('pointerup', end);
      track.addEventListener('pointerleave', end);
      track.addEventListener('click', function (e) { if (moved) { e.preventDefault(); } }, true);

      // Autoplay (reduced-motion aware). data-autoplay = interval in ms, 0 = off.
      var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      var interval = parseInt(root.getAttribute('data-autoplay'), 10) || 0;
      var loop = root.getAttribute('data-loop') === 'true';
      if (interval > 0 && !reduce) {
        var timer = setInterval(advance, interval);
        function advance() {
          var max = track.scrollWidth - track.clientWidth - 1;
          if (track.scrollLeft >= max) {
            if (loop) moveTo(0);
          } else {
            stepBy(1);
          }
        }
        // Pause on hover / focus / touch for usability.
        ['pointerenter', 'focusin'].forEach(function (ev) { root.addEventListener(ev, function () { clearInterval(timer); }); });
        ['pointerleave', 'focusout'].forEach(function (ev) { root.addEventListener(ev, function () { clearInterval(timer); timer = setInterval(advance, interval); }); });
      }
    });
  });
})();
